import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumb, Button, Card, CardHeader, CardContent, DeleteConfirmationModal, PageToolbar, Select, StatusPill, Switch } from '@/shared/components/ui';
import { useEmailDomain, useEmailDomainMutations } from '../hooks';
import { DomainStatusBadge } from '../components/DomainStatusBadge';
import { DnsRecordsTable } from '../components/DnsRecordsTable';
import { trackingDnsState, type TrackingDnsState } from '../utils/dnsRecords';
import type { UpdateEmailDomainRequest } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
/**
 * Pagina de detalhes de um dominio de email (tipo DETALHE/PERFIL).
 *
 * Anatomia (Design System):
 * - Toolbar 3 zonas com breadcrumb (Configuracoes > Dominios de e-mail > dominio) + acoes (Verificar, Excluir)
 * - Card de identidade (icone + dominio + datas + status)
 * - Card de registros DNS (com copiar)
 * - Card de configuracoes (tracking, TLS, envio/recebimento) com status de verificacao
 * - Card de informacoes (metadados)
 *
 * @example
 * ```tsx
 * <Route path="settings/email-domains/:id" element={<EmailDomainDetailPage />} />
 * ```
 */
export const EmailDomainDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: domain, isLoading } = useEmailDomain(id || '');
  const { updateDomain, deleteDomain, verifyDomain } = useEmailDomainMutations();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const handleUpdate = useCallback((updates: UpdateEmailDomainRequest) => {
    if (!id) return;
    updateDomain.mutate({ id, data: updates });
  }, [id, updateDomain]);

  const handleVerify = useCallback(() => {
    if (!id) return;
    verifyDomain.mutate(id);
  }, [id, verifyDomain]);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    try {
      await deleteDomain.mutateAsync(id);
      navigate('/settings/email-domains');
    } catch {
      // Erro tratado pelo React Query
    }
  }, [id, deleteDomain, navigate]);

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(dateStr));
    } catch {
      return '-';
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        className="h-full flex flex-col"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
        data-testid="email-domain-detail-loading"
      >
        <ToolbarShell currentLabel="Carregando..." onCrumbSettings={() => navigate('/settings')} onCrumbList={() => navigate('/settings/email-domains')} />
        <div className="flex-1 overflow-auto">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
            <div className="animate-pulse space-y-6">
              <div className="h-24 rounded-xl" style={{ backgroundColor: 'var(--theme-card-border)' }} />
              <div className="h-64 rounded-xl" style={{ backgroundColor: 'var(--theme-card-border)' }} />
              <div className="h-48 rounded-xl" style={{ backgroundColor: 'var(--theme-card-border)' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not found
  if (!domain) {
    return (
      <div
        className="h-full flex flex-col"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
        data-testid="email-domain-detail-notfound"
      >
        <ToolbarShell currentLabel="Nao encontrado" onCrumbSettings={() => navigate('/settings')} onCrumbList={() => navigate('/settings/email-domains')} />
        <div className="flex-1 overflow-auto">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-20 text-center">
            <Icon name="mail" className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--theme-muted-text)' }} />
            <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--theme-card-text)' }}>
              Dominio nao encontrado
            </h2>
            <p className="text-sm mb-6" style={{ color: 'var(--theme-muted-text)' }}>
              O dominio solicitado nao existe ou foi removido.
            </p>
            <Button
              variant="primary"
              icon="arrow-left"
              onClick={() => navigate('/settings/email-domains')}
              data-testid="email-domain-notfound-back"
            >
              Voltar para Dominios
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const trackingDns = trackingDnsState(domain.dnsRecords);

  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="email-domain-detail-page"
    >
      {/* Toolbar shared 3 zonas — token header, 56px (breadcrumb + acoes) */}
      <PageToolbar
        data-testid="email-domain-detail-toolbar"
        left={
          /* F25–F31: breadcrumb shared, nunca seta voltar */
          <Breadcrumb
            items={[
              { label: 'Configuracoes', onNavigate: () => navigate('/settings'), testid: 'email-domain-crumb-settings' },
              { label: 'Dominios de e-mail', onNavigate: () => navigate('/settings/email-domains'), testid: 'email-domain-crumb-list' },
              { label: domain.domain, testid: 'email-domain-crumb-current' },
            ]}
            mobileLegend="Configuracoes · Dominios de e-mail"
            data-testid="email-domain-breadcrumb"
          />
        }
        right={
          <>
            <Button
              variant="error"
              icon="trash"
              collapsibleLabel
              className="flex-none"
              onClick={() => setIsDeleteOpen(true)}
              title="Excluir dominio"
              data-testid="email-domain-delete-button"
            >
              Excluir dominio
            </Button>

            <Button
              variant="secondary"
              icon="refresh"
              collapsibleLabel
              className="flex-none"
              onClick={handleVerify}
              loading={verifyDomain.isPending}
              title="Verificar"
              data-testid="email-domain-verify-button"
            >
              Verificar
            </Button>
          </>
        }
      />

      {/* Conteudo */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 space-y-6">
          {/* Card de identidade / status */}
          <Card>
            <CardContent className="px-4 py-4">
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'var(--theme-primary-background)' }}
                >
                  <Icon name="mail" className="w-6 h-6" style={{ color: 'var(--theme-primary)' }} />
                </div>
                <div className="min-w-0 flex-1">
                  <h1
                    className="text-lg font-bold truncate"
                    style={{ color: 'var(--theme-header-text, var(--theme-card-text))' }}
                    title={domain.domain}
                  >
                    {domain.domain}
                  </h1>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                    Criado em {formatDate(domain.createdAt)}
                    {domain.lastVerificationAt && (
                      <> &middot; Ultima verificacao: {formatDate(domain.lastVerificationAt)}</>
                    )}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <DomainStatusBadge status={domain.status} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Registros DNS */}
          <Card>
            <CardHeader className="pb-2 py-3 px-4">
              <div className="flex items-center gap-2">
                <Icon name="shield" className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
                <h2 className="text-base font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                  Registros DNS
                </h2>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                Configure estes registros no seu provedor de DNS para verificar a propriedade do dominio.
              </p>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <DnsRecordsTable records={domain.dnsRecords} domain={domain.domain} />
            </CardContent>
          </Card>

          {/* Configuracoes */}
          <Card>
            <CardHeader className="pb-2 py-3 px-4">
              <h2 className="text-base font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                Configuracoes
              </h2>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="space-y-4">
                {/* Sending */}
                <SettingRow
                  icon={'send'}
                  label="Envio de Emails"
                  description="Permite enviar emails a partir deste dominio"
                  checked={domain.sendingEnabled}
                  onChange={(val) => handleUpdate({ sendingEnabled: val })}
                  isUpdating={updateDomain.isPending}
                  statusLabel={domain.sendingVerified ? 'Verificado' : 'Nao verificado'}
                  statusVerified={domain.sendingVerified}
                  testId="sending"
                />

                <div className="border-t" style={{ borderColor: 'var(--theme-card-border)' }} />

                {/* Receiving */}
                <SettingRow
                  icon={'inbox'}
                  label="Recebimento de Emails"
                  description="Permite receber emails neste dominio"
                  checked={domain.receivingEnabled}
                  onChange={(val) => handleUpdate({ receivingEnabled: val })}
                  isUpdating={updateDomain.isPending}
                  statusLabel={domain.receivingVerified ? 'Verificado' : 'Nao verificado'}
                  statusVerified={domain.receivingVerified}
                  testId="receiving"
                />

                <div className="border-t" style={{ borderColor: 'var(--theme-card-border)' }} />

                {/* Open Tracking */}
                <SettingRow
                  icon={'eye'}
                  label="Rastreamento de Abertura"
                  description={trackingDescription('Rastreia quando os destinatarios abrem os emails', domain.openTracking, trackingDns)}
                  checked={domain.openTracking}
                  onChange={(val) => handleUpdate({ openTracking: val })}
                  isUpdating={updateDomain.isPending}
                  {...trackingStatusProps(domain.openTracking, trackingDns)}
                  testId="open-tracking"
                />

                <div className="border-t" style={{ borderColor: 'var(--theme-card-border)' }} />

                {/* Click Tracking */}
                <SettingRow
                  icon={'mouse-pointer'}
                  label="Rastreamento de Cliques"
                  description={trackingDescription('Rastreia cliques nos links dos emails', domain.clickTracking, trackingDns)}
                  checked={domain.clickTracking}
                  onChange={(val) => handleUpdate({ clickTracking: val })}
                  isUpdating={updateDomain.isPending}
                  {...trackingStatusProps(domain.clickTracking, trackingDns)}
                  testId="click-tracking"
                />

                <div className="border-t" style={{ borderColor: 'var(--theme-card-border)' }} />

                {/* TLS Setting */}
                <div className="flex items-center justify-between gap-3 py-1">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--theme-primary-background)' }}
                    >
                      <Icon name="lock" className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                        Seguranca TLS
                      </p>
                      <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                        Define o nivel de seguranca para conexoes de email
                      </p>
                    </div>
                  </div>

                  <div className="flex-shrink-0" style={{ minWidth: 170 }}>
                    <Select
                      value={domain.tls}
                      onChange={(val) => handleUpdate({ tls: val as 'opportunistic' | 'enforced' })}
                      disabled={updateDomain.isPending}
                      options={[
                        { value: 'opportunistic', label: 'Oportunistico' },
                        { value: 'enforced', label: 'Forcado' },
                      ]}
                      data-testid="email-domain-tls-select"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informacoes */}
          <Card>
            <CardHeader className="pb-2 py-3 px-4">
              <h2 className="text-base font-semibold" style={{ color: 'var(--theme-card-text)' }}>
                Informacoes
              </h2>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <InfoItem label="Dominio" value={domain.domain} />
                <InfoItem label="Regiao" value={domain.region || 'Padrao'} />
                <InfoItem label="Criado em" value={formatDate(domain.createdAt)} />
                <InfoItem label="Atualizado em" value={formatDate(domain.updatedAt)} />
                {domain.verifiedAt && (
                  <InfoItem label="Verificado em" value={formatDate(domain.verifiedAt)} />
                )}
                {domain.returnPath && (
                  <InfoItem label="Return Path" value={domain.returnPath} />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        message={`Tem certeza que deseja excluir o dominio "${domain.domain}"?`}
        warningMessage="Todos os registros DNS associados serao removidos e emails neste dominio deixarao de funcionar."
        isLoading={deleteDomain.isPending}
      />
    </div>
  );
};

EmailDomainDetailPage.displayName = 'EmailDomainDetailPage';

// ============================================================================
// Rastreamento: ligado so vale depois que o registro de DNS e verificado
// ============================================================================

/** Selo ao lado do nome: so aparece com o rastreamento ligado e registro de DNS proprio. */
function trackingStatusProps(
  enabled: boolean,
  dns: TrackingDnsState,
): { statusLabel?: string; statusVerified?: boolean } {
  if (!enabled || dns === 'none') return {};
  return dns === 'verified'
    ? { statusLabel: 'Ativo', statusVerified: true }
    : { statusLabel: 'Aguardando DNS', statusVerified: false };
}

function trackingDescription(base: string, enabled: boolean, dns: TrackingDnsState): string {
  if (enabled && dns === 'pending') {
    return `${base}. Ainda nao esta valendo: adicione o registro "Rastreamento" acima no seu DNS e clique em Verificar`;
  }
  return base;
}

// ============================================================================
// Sub-components
// ============================================================================

interface ToolbarShellProps {
  currentLabel: string;
  onCrumbSettings: () => void;
  onCrumbList: () => void;
}

/** Toolbar shared com breadcrumb — usada nos estados loading / not-found. */
const ToolbarShell: React.FC<ToolbarShellProps> = ({ currentLabel, onCrumbSettings, onCrumbList }) => {
  return (
    <PageToolbar
      left={
        <Breadcrumb
          items={[
            { label: 'Configuracoes', onNavigate: onCrumbSettings },
            { label: 'Dominios de e-mail', onNavigate: onCrumbList },
            { label: currentLabel },
          ]}
          mobileLegend="Configuracoes · Dominios de e-mail"
        />
      }
    />
  );
};

ToolbarShell.displayName = 'ToolbarShell';

interface SettingRowProps {
  icon: IconName;
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  isUpdating: boolean;
  statusLabel?: string;
  statusVerified?: boolean;
  testId: string;
}

const SettingRow: React.FC<SettingRowProps> = ({
  icon,
  label,
  description,
  checked,
  onChange,
  isUpdating,
  statusLabel,
  statusVerified,
  testId,
}) => {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--theme-primary-background)' }}
        >
          <Icon name={icon} className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
              {label}
            </p>
            {statusLabel && (
              <StatusPill
                variant={statusVerified ? 'success' : 'neutral'}
                label={statusLabel}
                data-testid={`email-domain-status-${testId}`}
              />
            )}
          </div>
          <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            {description}
          </p>
        </div>
      </div>

      {/* Switch shared 38×22 (F82) */}
      <span className="flex-shrink-0" data-testid={`email-domain-toggle-${testId}`}>
        <Switch checked={checked} onChange={onChange} disabled={isUpdating} />
      </span>
    </div>
  );
};

SettingRow.displayName = 'SettingRow';

interface InfoItemProps {
  label: string;
  value: string;
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  return (
    <div>
      <dt className="text-xs font-medium mb-0.5" style={{ color: 'var(--theme-muted-text)' }}>
        {label}
      </dt>
      <dd className="text-sm font-medium break-all" style={{ color: 'var(--theme-card-text)' }}>
        {value}
      </dd>
    </div>
  );
};

InfoItem.displayName = 'InfoItem';
